<template>
  <div
    class="dj-product-container flex flex-wrap items-start sm:flex-nowrap"
    :class="{ active: open }"
  >
    <DjImage
      :src="mainPictureUrl || ''"
      :width="100"
      :height="100"
      :alt="productName"
      class="dj-product-image w-12 h-12 sm:w-16 sm:h-16"
    />
    <div class="dj-product-details flex-1 pl-4 ">
      <h3 class="dj-product-name" :class="{ 'max-w-[180px]': small, 'max-w-[540px]': !small }">
        {{ productName }}
      </h3>
      <p class="dj-product-material">
        {{ matiere }}
      </p>

      
      <!-- ✅ Nouvelles informations d'offre -->
      <div class="dj-offer-info space-y-1">
        <div class="dj-price flex items-center">
          <Icon name="heroicons:currency-euro" size="16" class="mr-1 text-gray-500" />
          <span class="font-semibold text-gray-500">{{ formatPrice(offerPrice) }}</span>
          <span class="text-sm text-gray-500 ml-1">HT</span>
        </div>
        <div class="dj-stock flex items-center text-sm">
          <Icon name="heroicons:cube" size="14" class="mr-1 text-gray-500" />
          <span :class="stockClass">{{ stockText }}</span>
        </div>
      </div>
    </div>
    
    <!-- ✅ Boutons d'action -->
    <div class="dj-actions flex flex-col gap-2">
      <!-- Bouton d'expansion (existant) -->
      <UButton
        v-if="isAuthenticated && hasVariants"
        icon="heroicons:eye"
        label="Offres"
        size="sm"
        color="primary"
        variant="outline"
        class="dj-product-action"
        :class="{ active: open }"
        @click.stop="selectProduct"
        @mouseover="isOver = true"
        @mouseout="isOver = false"
      />
      
      <!-- ✅ Nouveau bouton d'ajout au panier -->
      <DjAddToCartButton
        v-if="isAuthenticated && canAddToCart"
        :cart-id="cartId"
        type-id="offerPriceId"
        :lines="cartLines"
        :is-authenticated="isAuthenticated"
        :is-disabled="!hasStock"
        :label="addToCartLabel"
        size="sm"
        icon="heroicons:shopping-cart"
        class="dj-add-cart-btn"
        @added-to-cart="handleAddedToCart"
        @error-to-cart="handleErrorToCart"
        @click.stop
      />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useSearchProduct } from '~/composables/useSearchProduct';

  const props = defineProps<{
    product: ProductSearchContent;
    open: boolean;
    small: boolean;
    hasVariants: boolean;
  }>();

  const router = useRouter();
  const { 
    getAttributeValuesByExternalId, 
    getProductName, 
    getMainPictureUrl,
    getOfferUnitPrice,
    getOfferStock,
    getOfferMinQuantity,
    getOfferPrice,
    getVariant
  } = useSearchProduct();
  
  // ✅ Composables pour le panier
  const { getCartId } = useCart();
  const cartStore = useCartStore();
  const cart = computed(() => cartStore.cart);
  const cartId = computed(() => getCartId(cart.value));
  
  const useStore = useUserStore();
  const isAuthenticated = computed(() => useStore.isAuthenticated);
  const isOver = ref(false);
  const quantity = ref(1);

  // Computed product name
  const productName = computed(() => getProductName(props.product));

  // Computed product main picture url
  const mainPictureUrl = computed(() => getMainPictureUrl(props.product));

  // Computed attribute values by external id
  const matiere = computed(() => getAttributeValuesByExternalId(props.product, 'matiere'));
  const nb_variants = computed(() => getAttributeValuesByExternalId(props.product, 'nb_variants'));
  const nb_variants_text = computed(() => {
    const variantsCount = parseInt(nb_variants.value ?? '0', 10);
    return `DISPONIBLE EN ${variantsCount} ${variantsCount > 1 ? 'MODÈLES' : 'MODÈLE'}`;
  });
  
  // ✅ Nouvelles computed pour les informations d'offre
  const offerPrice = computed(() => getOfferUnitPrice(props.product));
  const stock = computed(() => getOfferStock(props.product));
  const minQuantity = computed(() => getOfferMinQuantity(props.product));
  const offerPriceData = computed(() => getOfferPrice(props.product));
  const variant = computed(() => getVariant(props.product));
  
  // ✅ Computed pour l'affichage du stock
  const hasStock = computed(() => stock.value > 0);
  const stockText = computed(() => {
    if (stock.value > 10) return `En stock (${stock.value})`;
    if (stock.value > 0) return `Stock faible (${stock.value})`;
    return 'Rupture de stock';
  });
  
  const stockClass = computed(() => {
    if (stock.value > 10) return 'text-green-600';
    if (stock.value > 0) return 'text-orange-600';
    return 'text-red-600';
  });
  
  // ✅ Computed pour le panier
  const canAddToCart = computed(() => hasStock.value && offerPriceData.value?.id);
  
  const cartLines = computed(() => [{
    variantId: variant.value?.id,
    offerPriceId: offerPriceData.value?.externalId,
    quantity: Math.max(quantity.value, minQuantity.value || 1),
    updateAction: 'ADD_QUANTITY' as const
  }]);
  
  const addToCartLabel = computed(() => {
    const qty = Math.max(quantity.value, minQuantity.value || 1);
    return qty > 1 ? `Ajouter (${qty})` : 'Ajouter';
  });
  
  // ✅ Utilitaire de formatage du prix
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(price);
  };

  const localePath = useLocalePath();

  const emit = defineEmits<{
    'select-product': [product: ProductSearchContent]; // named tuple syntax
    'go-to-product': any;
    'added-to-cart': [data: any]; // ✅ Nouvel événement
    'error-to-cart': [error: any]; // ✅ Nouvel événement
  }>();

  const selectProduct = () => {
    emit('select-product', props.product);
  };

  
  // ✅ Gestionnaires pour l'ajout au panier
  const handleAddedToCart = (data: any) => {
    console.log('Produit ajouté au panier:', data);
    emit('added-to-cart', data);
  };

  const handleErrorToCart = (error: any) => {
    console.error('Erreur ajout au panier:', error);
    emit('error-to-cart', error);
  };
</script>

<style lang="scss" scoped>
  @use './DjProductSearchResult.scss';
</style>
